import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { UserInfoService } from '../../services/UserInfoService/user-info.service';
import { loadingDialog } from '../../dialog';
import Swal from 'sweetalert2';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private isRefreshing = false;

  constructor(
    private userInfoService: UserInfoService,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let authReq = request;
    const token = this.userInfoService.getAccessToken();
    if (token != null) {
      authReq = this.addToken(request, token);
    }
    loadingDialog();
    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(authReq, next);
        } else {
          if (error instanceof HttpErrorResponse) {
            console.error('Error status: ', error.status);
          }
          return throwError(error);
        }
      }),
      finalize(() => {
        if (Swal.isLoading()) {
          Swal.close();
        }
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.userInfoService.refreshToken().pipe(
        switchMap((tokenResponse: any) => {
          this.isRefreshing = false;
          const newToken = tokenResponse.accessToken;
          this.userInfoService.setAccessToken(newToken);
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((error: any) => {
          this.logoutAndNavigateToProfile();
          this.router.navigate(['/ar/login']);
          return throwError(error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private logoutAndNavigateToProfile(): void {
    if (this.userInfoService.isUserLoggedIn()) {
      this.userInfoService.clearUserInfo();
      this.router.navigate(['/ar/login']);
    }
  }
}
